/* eslint-disable @typescript-eslint/no-empty-function */
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  LoginCredentials,
  AuthResponse,
  APIError,
  User,
  VerifyData,
  ResetPasswordData,
  RegisterFormData,
  ForgotPasswordFormData,
  OnboardingFormData,
} from './types';
import { useCookies } from 'src/utils';
import axiosInstance from 'lib/backend-provider';
import { isAxiosError } from 'axios';

export const registerUser = createAsyncThunk<
  AuthResponse,
  RegisterFormData,
  { rejectValue: APIError }
>(
  'user/register',
  async (registerData: RegisterFormData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post<AuthResponse>(
        '/register',
        registerData,
      );

      const { setEmailCookie } = useCookies();
      setEmailCookie(response.data.user.email);
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data as APIError);
      }
      return rejectWithValue({ detail: 'An unexpected error occurred' });
    }
  },
);

export const loginUser = createAsyncThunk<
  AuthResponse,
  LoginCredentials,
  { rejectValue: APIError }
>('user/login', async (loginData: LoginCredentials, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<AuthResponse>(
      `/login`,
      loginData,
    );
    const { setLoginCookies } = useCookies();
    setLoginCookies(response.data.access_token, response.data.refresh_token);

    fetch('/api/notifications/user-logged-in', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        first_name: response.data.user.first_name,
        last_name: response.data.user.last_name,
        email: response.data.user.email,
      }),
    }).catch(() => {});

    return response.data;
  } catch (error: unknown) {
    if (isAxiosError(error) && error.response) {
      return rejectWithValue(error.response.data as APIError);
    }
    return rejectWithValue({ detail: 'An unexpected error occurred' });
  }
});

export const getUser = createAsyncThunk<
  User,
  { accessToken: string } | void,
  { rejectValue: APIError }
>(
  'user/getUser',
  async (params, { rejectWithValue }) => {
    const token =
      params && 'accessToken' in params
        ? params.accessToken
        : useCookies().getAccessToken();
    try {
      const response = await axiosInstance.get<User>(`/users/current-user`, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data as APIError);
      }
      return rejectWithValue({ detail: 'Failed to fetch user data' });
    }
  },
);

export const verifyUser = createAsyncThunk<
  void,
  VerifyData,
  { rejectValue: APIError }
>('user/verify', async (verifyData: VerifyData, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<void>(`/validate`, verifyData, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error: unknown) {
    if (isAxiosError(error) && error.response) {
      return rejectWithValue(error.response.data as APIError);
    }
    return rejectWithValue({ detail: 'Verification failed' });
  }
});

export const forgotPassword = createAsyncThunk<
  void,
  ForgotPasswordFormData,
  { rejectValue: APIError }
>(
  'user/forgotPassword',
  async (data: ForgotPasswordFormData, { rejectWithValue }) => {
    try {
      await axiosInstance.post(`/verify-reset-password`, data, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data as APIError);
      }
      return rejectWithValue({ detail: 'An unexpected error occurred' });
    }
  },
);

export const resetPassword = createAsyncThunk<
  void,
  ResetPasswordData,
  { rejectValue: APIError }
>(
  'user/resetPassword',
  async (resetData: ResetPasswordData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/validate-reset-password`,
        resetData,
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data as APIError);
      }
      return rejectWithValue({ detail: 'Reset password failed' });
    }
  },
);

export const updateUserOnboarding = createAsyncThunk<
  User,
  OnboardingFormData,
  { rejectValue: APIError }
>(
  'user/updateOnboarding',
  async (data: OnboardingFormData, { rejectWithValue }) => {
    const { getAccessToken } = useCookies();
    const accessToken = getAccessToken();
    try {
      const response = await axiosInstance.patch(`/users`, data, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data as APIError);
      }
      return rejectWithValue({ detail: 'Failed to update user data' });
    }
  },
);

export const socialLogin = createAsyncThunk(
  'user/socialLogin',
  async (
    {
      provider,
      code,
      redirectUri,
    }: { provider: string; code: string; redirectUri?: string },
    { rejectWithValue },
  ) => {
    try {
      const requestBody: {
        provider: string;
        code: string;
        redirect_uri?: string;
      } = {
        provider,
        code,
      };

      // Include redirect_uri if provided (required by some OAuth providers for token exchange)
      if (redirectUri) {
        requestBody.redirect_uri = redirectUri;
      }

      const backendResponse = await axiosInstance.post<AuthResponse>(
        `/social-login`,
        requestBody,
      );

      const { setLoginCookies } = useCookies();
      setLoginCookies(
        backendResponse.data.access_token,
        backendResponse.data.refresh_token,
      );

      fetch('/api/notifications/user-logged-in', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: backendResponse.data.user.first_name,
          last_name: backendResponse.data.user.last_name,
          email: backendResponse.data.user.email,
        }),
      }).catch(() => {});

      return backendResponse.data;
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response) {
        return rejectWithValue(error.response?.data as APIError);
      }

      return rejectWithValue({ detail: 'Social login failed' });
    }
  },
);
